import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
} from "react-bootstrap";
import { BiTrash } from "react-icons/bi";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getBatchCoachingPerformanceResult,
  editCoachingPerformance,
} from "../../actions/CoachingPerformance.action";
import { years } from "../../constants/MonthsAndYears";
import { toast } from "react-toastify";
import {
  getTermOptions,
  getTermLabel,
  getTermCountOptions,
} from "../../utils/coachingPerformance";
import styles from "./EditCoachingPerformanceForm.module.css";

const isValidTotalMark = (value) => {
  const parsedValue = parseFloat(value);
  return Number.isFinite(parsedValue) && parsedValue > 0;
};

const isValidGainedMark = (value) => {
  const parsedValue = parseFloat(value);
  return Number.isFinite(parsedValue) && parsedValue >= 0;
};

const EditCoachingPerformanceForm = ({
  grades,
  getBatchCoachingPerformanceResult,
  editCoachingPerformance,
  extra,
}) => {
  const [grade, setGrade] = useState("");
  const [batch, setBatch] = useState("");
  const [shift, setShift] = useState("");
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [loading, setLoading] = useState(false);
  const [termCount, setTermCount] = useState("");
  const [term, setTerm] = useState("");

  const [students, setStudents] = useState(null);

  const navigate = useNavigate();

  const updateStudentField = (studentId, field, value) => {
    setStudents(
      students.map((student) =>
        student.id === studentId
          ? {
              ...student,
              [field]: value,
            }
          : student
      )
    );
  };

  const selectHandeler = async () => {
    if (!term) {
      toast.error("Please select term type first!");
      return;
    }
    const list = await getBatchCoachingPerformanceResult(year, term, batch);
    if (!list || list.length === 0) {
      setStudents([]);
      return;
    }
    
    // Determine term_count from first student's data
    if (list.length > 0 && list[0].term_count) {
      setTermCount(list[0].term_count.toString());
    }
    
    setStudents(
      list.map((l) => ({
        id: l.coachingPerformances.length > 0 ? l.coachingPerformances[0].id : 0,
        studentId: l.id,
        uid: l.uid,
        name: l.name,
        phone: l.phone,
        term_count: l.term_count,
        gained:
          l.coachingPerformances.length > 0
            ? l.coachingPerformances[0].gained.toString()
            : "0",
        total:
          l.coachingPerformances.length > 0
            ? l.coachingPerformances[0].total.toString()
            : "0",
      }))
    );
    
    // Set term from first student's coaching performance if available
    if (list.length > 0 && list[0].coachingPerformances.length > 0) {
      setTerm(list[0].coachingPerformances[0].term.toString());
    }
  };

  useEffect(() => {
    if (students && students.length > 0 && !termCount) {
      // Set term_count from student data
      setTermCount(students[0].term_count.toString());
    }
    if (students && students.length > 0 && !term) {
      // Try to get term from extra data
      if (extra && extra.term) {
        setTerm(extra.term.toString());
      }
    }
  }, [students, termCount, term, extra]);

  const submitHandeler = async () => {
    if (!students || students.length === 0) {
      toast.error("Please select at least one student!");
      return;
    }

    const invalidStudent = students.find(
      (student) =>
        !isValidTotalMark(student.total) || !isValidGainedMark(student.gained)
    );

    if (invalidStudent) {
      toast.error(
        `Please enter valid total and gained marks for ${invalidStudent.name}.`
      );
      return;
    }

    const normalizedStudents = students.map((student) => ({
      ...student,
      total: parseFloat(student.total),
      gained: parseFloat(student.gained),
    }));

    setLoading(true);

    let check = await editCoachingPerformance(term, year, normalizedStudents);
    if (check === true) {
      setLoading(false);
      navigate("/admin/coaching-performance/view");
    } else {
      setLoading(false);
    }
  };

  const deleteHandeler = (id) => {
    setStudents([...students.filter((st) => st.id !== id)]);
  };

  // Get term options based on selected term count
  const termOptions = termCount ? getTermOptions(parseInt(termCount)) : [];

  return (
    <Container>
      <Card bg="white" text="dark" className={`crd shadow`}>
        <Card.Body>
          <Row>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="grade" className="d-block">
                  Class
                </label>
              </div>
              <Form.Select
                onChange={(e) => {
                  setBatch("");
                  setShift("");
                  setGrade(e.target.value);
                }}
              >
                <option value={""}>Select Class</option>
                {grades &&
                  grades.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </Form.Select>
            </Col>
            {grade !== "" ? (
              <Col md={3} className="py-3">
                <div className="d-flex justify-content-between align-items-center pb-2">
                  <label htmlFor="shift" className="d-block">
                    Shift
                  </label>
                </div>
                <Form.Select
                  onChange={(e) => {
                    setBatch("");
                    setShift(e.target.value);
                  }}
                >
                  <option value={""}>Select Shift</option>
                  {grades
                    .filter((grd) => grd.id === parseInt(grade))[0]
                    .shifts.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                </Form.Select>
              </Col>
            ) : (
              <></>
            )}
            {grade !== "" && shift !== "" ? (
              <Col md={3} className="py-3">
                <div className="d-flex justify-content-between align-items-center pb-2">
                  <label htmlFor="batch" className="d-block">
                    Batch
                  </label>
                </div>
                <Form.Select onChange={(e) => setBatch(e.target.value)}>
                  <option value={""}>Select Batch</option>
                  {grades
                    .filter((grd) => grd.id === parseInt(grade))[0]
                    .batches.filter((bt) => bt.shiftId === parseInt(shift))
                    .map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                </Form.Select>
              </Col>
            ) : (
              <></>
            )}
            {grade !== "" && shift !== "" && batch !== "" ? (
              <Col
                md={3}
                className="d-flex jsutify-content-end align-items-end py-3"
              >
                <Button
                  onClick={selectHandeler}
                  variant="primary"
                  type="submit"
                  className="btn_primary"
                >
                  Select
                </Button>
              </Col>
            ) : (
              <></>
            )}
          </Row>
          <Row>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="termCount" className="d-block">
                  Term Count
                </label>
              </div>
              <Form.Select
                onChange={(e) => {
                  setTermCount(e.target.value);
                  // Reset term if new term count doesn't support current term
                  if (term && parseInt(term) > parseInt(e.target.value)) {
                    setTerm("");
                  }
                }}
                id="termCount"
                name="termCount"
                value={termCount}
              >
                <option value={""}>Select Term Count</option>
                {getTermCountOptions().map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Col>
            {termCount !== "" ? (
              <Col md={3} className="py-3">
                <div className="d-flex justify-content-between align-items-center pb-2">
                  <label htmlFor="term" className="d-block">
                    Term Type
                  </label>
                </div>
                <Form.Select
                  onChange={(e) => {
                    setTerm(e.target.value);
                  }}
                  id="term"
                  name="term"
                  value={term}
                >
                  <option value={""}>Select Term Type</option>
                  {termOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            ) : (
              <></>
            )}
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="year" className="d-block">
                  Year
                </label>
              </div>
              <Form.Select
                onChange={(e) => {
                  setYear(e.target.value);
                }}
                id="year"
                name="year"
                value={year}
              >
                {years.map((item, i) => (
                  <option key={i} value={`${item}`}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      {students ? (
        <Card bg="white" text="dark" className={`crd shadow mt-4`}>
          <Card.Body>
            <h1 className="fs-4 fw-normal py-3">
              Fill the student marks{" "}
              <b>
                {extra && extra?.term && extra?.termCount && extra?.year
                  ? `for ${getTermLabel(parseInt(extra.termCount), parseInt(extra.term))} ${extra.year}`
                  : termCount && term && year
                  ? `for ${getTermLabel(parseInt(termCount), parseInt(term))} ${year}`
                  : ""}
              </b>
            </h1>
            <Row>
              <Col
                md={1}
                className="d-flex align-items-center justify-content-center"
              >
                <span className="d-block fw-bold">Roll</span>
              </Col>
              <Col md={4} className="d-flex align-items-center ">
                <span className="d-block fw-bold">Name</span>
              </Col>
              <Col md={3} className="d-flex align-items-center ">
                <span className="d-block fw-bold">Total Mark</span>
              </Col>
              <Col md={3} className="d-flex align-items-center ">
                <span className="d-block fw-bold">Gained Mark</span>
              </Col>

              <Col md={1} className="d-flex align-items-center ">
                <span className="d-block fw-bold">Delete</span>
              </Col>
            </Row>
            <hr />
            {students
              .sort(function (a, b) {
                if (a.uid < b.uid) return -1;
                if (a.uid > b.uid) return 1;
                return 0;
              })
              .map((student) => (
                <Row key={student.id}>
                  <Col
                    md={1}
                    className="d-flex align-items-center justify-content-center"
                  >
                    <span className="d-block">
                      <span className="fw-bold">{student.uid}</span>
                    </span>
                  </Col>
                  <Col md={4} className="d-flex align-items-center">
                    <span className="d-block">
                      <span className="fw-bold">{student.name}</span>
                    </span>
                  </Col>

                  <Col md={3}>
                    <InputGroup className=" d-flex flex-column">
                      <Form.Control
                        placeholder="Total mark..."
                        name="total"
                        type="number"
                        required
                        className={`${styles.input} w-100`}
                        value={student.total}
                        step={0.01}
                        onChange={(e) =>
                          updateStudentField(student.id, "total", e.target.value)
                        }
                      />
                    </InputGroup>
                  </Col>
                  <Col md={3}>
                    <InputGroup className=" d-flex flex-column">
                      <Form.Control
                        placeholder="Gained mark..."
                        name="gained"
                        type="number"
                        required
                        className={`${styles.input} w-100`}
                        value={student.gained}
                        step={0.01}
                        onChange={(e) =>
                          updateStudentField(student.id, "gained", e.target.value)
                        }
                      />
                    </InputGroup>
                  </Col>

                  <Col md={1} className="d-flex align-items-center">
                    <span
                      className={`text-danger fs-4  ${styles.del}`}
                      onClick={() => deleteHandeler(student.id)}
                    >
                      <BiTrash />
                    </span>
                  </Col>
                  <hr className="mt-3 mx-auto" style={{ width: "93%" }} />
                </Row>
              ))}
            <Row>
              <Col md={6}></Col>
            </Row>
            <Button
              onClick={submitHandeler}
              variant="primary"
              className="btn_primary"
              disabled={loading}
            >
              {loading ? "Loading..." : "Submit"}
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <></>
      )}
    </Container>
  );
};

const mapStateToProps = (state) => ({
  extra: state.coachingPerformance.extra,
});
export default connect(mapStateToProps, {
  getBatchCoachingPerformanceResult,
  editCoachingPerformance,
})(EditCoachingPerformanceForm);
